import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import { FaLocationArrow, FaSearchLocation, FaRoute, FaSpinner, FaExclamationTriangle, FaPlus, FaMinus } from "react-icons/fa";
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/ToastContext';
import { CustomInputBox1, CustomButton6 } from '../../Templates';
import './EventRouteCalculator.css';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MAX_WAYPOINTS = 3; // Maximum number of waypoints (stops) allowed

const EventRouteCalculator = forwardRef(({ onRouteCalculated, eventLocation }, ref) => {
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  const mapRef = useRef(null);
  const fromInputRef = useRef(null);
  const waypointInputRefs = useRef([]);
  
  const [map, setMap] = useState(null);
  const [directionsService, setDirectionsService] = useState(null);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);
  const [fromValue, setFromValue] = useState("");
  const [waypoints, setWaypoints] = useState([]); // Array to store waypoints
  const [distance, setDistance] = useState(null);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);

  // First, check if Google Maps API is available
  useEffect(() => {
    const checkGoogleMapsLoaded = () => {
      if (window.google && window.google.maps) {
        setIsMapLoading(false);
      } else {
        setTimeout(checkGoogleMapsLoaded, 500);
      }
    };

    checkGoogleMapsLoaded();
  }, []);

  // Initialize the map
  useEffect(() => {
    if (isMapLoading || mapInitialized || !mapRef.current) return;

    const initTimer = setTimeout(() => {
      try {
        const defaultLocation = { lat: 56.9677, lng: 24.1056 }; // Default to Latvia
        
        // Initialize map
        const newMap = new window.google.maps.Map(mapRef.current, {
          center: defaultLocation,
          zoom: 7,
          fullscreenControl: false,
          mapTypeControl: true,
          streetViewControl: false,
        });

        const directionsService = new window.google.maps.DirectionsService();
        const directionsRenderer = new window.google.maps.DirectionsRenderer({
          map: newMap,
          suppressMarkers: true,
        });

        setMap(newMap);
        setDirectionsService(directionsService);
        setDirectionsRenderer(directionsRenderer);
        setMapInitialized(true);
      } catch (e) {
        console.error("Error initializing map:", e);
        setError(t('errors.map_initialization_failed') || "Failed to initialize map");
      }
    }, 100);
    
    return () => clearTimeout(initTimer);
  }, [isMapLoading, mapInitialized, t]);

  // Setup autocomplete for the starting point and waypoints
  useEffect(() => {
    if (!mapInitialized || !map) return;

    try {
      const defaultBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(55, 23),
        new window.google.maps.LatLng(58, 26)
      );

      // Autocomplete options
      const autocompleteOptions = {
        bounds: defaultBounds,
        strictBounds: false,
        componentRestrictions: { country: "lv" },
      };

      // Setup autocomplete for 'from' input
      if (fromInputRef.current) {
        const fromAutoComplete = new window.google.maps.places.Autocomplete(
          fromInputRef.current.querySelector("input"),
          autocompleteOptions
        );

        fromAutoComplete.addListener("place_changed", () => {
          const place = fromAutoComplete.getPlace();
          if (place.geometry) {
            setFromValue(place.formatted_address || place.name);
          }
        });
      }

      // Setup autocomplete for waypoints
      waypointInputRefs.current.forEach((ref, index) => {
        if (ref) {
          const waypointAutoComplete = new window.google.maps.places.Autocomplete(
            ref.querySelector("input"),
            autocompleteOptions
          );

          waypointAutoComplete.addListener("place_changed", () => {
            const place = waypointAutoComplete.getPlace();
            if (place.geometry) {
              updateWaypoint(index, place.formatted_address || place.name);
            }
          });
        }
      });
    } catch (e) {
      console.error("Error setting up autocomplete:", e);
      setError(t('errors.autocomplete_setup_failed') || "Failed to set up address search");
    }
  }, [mapInitialized, map, t, waypoints.length]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (directionsRenderer) {
        directionsRenderer.setMap(null);
      }
    };
  }, [directionsRenderer]);

  // Extract route polyline from directions
  const extractRoutePolyline = (directions) => {
    if (!directions || !directions.routes || directions.routes.length === 0) {
      return null;
    }
    
    try {
      // Standard format
      if (directions.routes[0].overview_polyline && 
          directions.routes[0].overview_polyline.points) {
        return directions.routes[0].overview_polyline.points;
      }
      
      // Alternative format
      if (directions.routes[0].overview_polyline && 
          typeof directions.routes[0].overview_polyline === 'string') {
        return directions.routes[0].overview_polyline;
      }
      
      // Last resort fallback
      if (directions.routes[0].legs && directions.routes[0].legs.length > 0) {
        const firstLeg = directions.routes[0].legs[0];
        const lastLeg = directions.routes[0].legs[directions.routes[0].legs.length - 1];
        
        if (firstLeg.start_location && lastLeg.end_location) {
          const minimalPolyline = `${firstLeg.start_location.lat().toFixed(6)},${firstLeg.start_location.lng().toFixed(6)}|${lastLeg.end_location.lat().toFixed(6)},${lastLeg.end_location.lng().toFixed(6)}`;
          return minimalPolyline;
        }
      }
      
      return null;
    } catch (e) {
      console.error("Error extracting route polyline:", e);
      return null;
    }
  };

  // Generate static map URL 
  const generateStaticMapUrl = (directions) => {
    if (!directions || !directions.routes || directions.routes.length === 0) {
      return null;
    }
    
    try {
      const path = directions.routes[0].overview_polyline;
      const start = directions.routes[0].legs[0].start_location;
      const end = directions.routes[0].legs[directions.routes[0].legs.length - 1].end_location;
      const encodedPath = encodeURIComponent(path);

      return `https://maps.googleapis.com/maps/api/staticmap?size=600x300&path=enc:${encodedPath}&markers=color:green%7Clabel:S%7C${start.lat()},${start.lng()}&markers=color:red%7Clabel:E%7C${end.lat()},${end.lng()}&key=${GOOGLE_MAPS_API_KEY}`;
    } catch (e) {
      console.error("Error generating static map URL:", e);
      return null;
    }
  };

  // Calculate the distance from directions
  const handleRouteCalculation = (response) => {
    if (response && response.routes && response.routes[0]) {
      const route = response.routes[0];
      const distanceInKm = route.legs.reduce((total, leg) => total + leg.distance.value, 0) / 1000;
      const roundedDistance = Number(distanceInKm.toFixed(2));
      return roundedDistance;
    }
    return null;
  };

  // Waypoint management functions
  const addWaypoint = () => {
    if (waypoints.length < MAX_WAYPOINTS) {
      setWaypoints([...waypoints, ""]);
    } else {
      addToast({
        type: 'warning',
        title: t('events.maximum_waypoints'),
        description: t('events.maximum_waypoints_description', { max: MAX_WAYPOINTS })
      });
    }
  };

  const removeWaypoint = (index) => {
    const newWaypoints = [...waypoints];
    newWaypoints.splice(index, 1);
    setWaypoints(newWaypoints);
  };

  const updateWaypoint = (index, value) => {
    const newWaypoints = [...waypoints];
    newWaypoints[index] = value;
    setWaypoints(newWaypoints);
  };

  // Validate inputs
  const validateInputs = () => {
    if (!fromValue) {
      setError(t('forms.departure_required') || "Please enter your departure location");
      addToast({
        type: 'error',
        title: t('errors.missing_addresses'),
        description: t('forms.departure_required')
      });
      return false;
    }
    
    if (!eventLocation) {
      setError(t('errors.missing_event_location') || "Event location is missing");
      addToast({
        type: 'error',
        title: t('errors.missing_addresses'),
        description: t('errors.missing_event_location')
      });
      return false;
    }
    
    return true;
  };

  // Calculate and display the route
  const calculateAndDisplayRoute = async () => {
    setIsCalculating(true);
    setError(null);
    
    try {
      // Validate inputs
      if (!validateInputs()) {
        setIsCalculating(false);
        return null;
      }
      
      if (!directionsService || !directionsRenderer) {
        setError(t('errors.maps_service_not_available') || "Maps service not available");
        setIsCalculating(false);
        return null;
      }

      // Prepare waypoints for the directions request
      const googleWaypoints = waypoints
        .filter(waypoint => waypoint.trim() !== '')
        .map(waypoint => ({
          location: waypoint,
          stopover: true
        }));

      return new Promise((resolve, reject) => {
        directionsService.route(
          {
            origin: fromValue,
            destination: eventLocation,
            waypoints: googleWaypoints,
            optimizeWaypoints: true, // Optimize the route if possible
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (response, status) => {
            if (status === "OK") {
              directionsRenderer.setDirections(response);
              const distanceKm = handleRouteCalculation(response);
              setDistance(distanceKm);
              
              const staticMapUrl = generateStaticMapUrl(response);
              const routePolyline = extractRoutePolyline(response);
              
              // Prepare waypoints data for saving to database
              const waypointsData = waypoints
                .filter(waypoint => waypoint.trim() !== '')
                .map(waypoint => waypoint.trim());
              
              // Pass data back to parent component
              if (onRouteCalculated) {
                onRouteCalculated({
                  distance_km: distanceKm,
                  static_map_url: staticMapUrl,
                  route_polyline: routePolyline,
                  address_from: {
                    street: fromValue,
                    city: '',
                    country: '',
                  },
                  waypoints: waypointsData
                });
              }
              
              setIsCalculating(false);
              resolve(distanceKm);
            } else {
              setError(t('errors.route_calculation_failed') || "Failed to calculate route");
              setIsCalculating(false);
              reject(new Error(status));
            }
          }
        );
      });
    } catch (error) {
      setError(error.message || t('errors.route_calculation_failed'));
      setIsCalculating(false);
      throw error;
    }
  };

  // Expose function to parent component
  useImperativeHandle(ref, () => ({
    calculateAndDisplayRoute
  }));

  // Handle calculate button click
  const handleCalculateClick = () => {
    try {
      calculateAndDisplayRoute()
        .then(distance => {
          if (distance) {
            addToast({
              type: 'success',
              title: t('events.route_calculated'),
              description: t('events.route_distance_calculated', { distance })
            });
          }
        })
        .catch(err => {
          console.error('Error calculating route:', err);
          addToast({
            type: 'error',
            title: t('errors.route_error'),
            description: err.message || t('errors.route_calculation_failed')
          });
        });
    } catch (err) {
      console.error('Error triggering route calculation:', err);
    }
  };

  if (isMapLoading) {
    return (
      <div className="event-route-loading">
        <FaSpinner className="spinner" />
        <p>{t('app.loading')}</p>
      </div>
    );
  }

  return (
    <div className="event-route-calculator">
      {error && (
        <div className="event-route-error">
          <FaExclamationTriangle />
          <span>{error}</span>
        </div>
      )}
      
      <div className="event-route-inputs">
        <div className="from-input-container">
          <label>{t('events.departure_point')}</label>
          <div className="input-group" ref={fromInputRef}>
            <div className="input-icon">
              <FaLocationArrow />
            </div>
            <CustomInputBox1
              placeholder={t('events.enter_departure_location')}
              value={fromValue}
              onChange={(e) => setFromValue(e.target.value)}
            />
          </div>
        </div>
        
        {/* Waypoints (extra stops) */}
        {waypoints.map((waypoint, index) => (
          <div className="waypoint-input-container" key={`waypoint-${index}`}>
            <label>{t('events.waypoint', { number: index + 1 })}</label>
            <div className="input-group" ref={el => waypointInputRefs.current[index] = el}>
              <div className="input-icon">
                <FaSearchLocation />
              </div>
              <CustomInputBox1
                placeholder={t('events.enter_waypoint_location')}
                value={waypoint}
                onChange={(e) => updateWaypoint(index, e.target.value)}
              />
              <button 
                className="remove-waypoint-btn"
                onClick={() => removeWaypoint(index)}
                title={t('events.remove_waypoint')}
              >
                <FaMinus />
              </button>
            </div>
          </div>
        ))}
        
        {/* Add Waypoint Button */}
        {waypoints.length < MAX_WAYPOINTS && (
          <div className="add-waypoint-container">
            <button 
              className="add-waypoint-btn"
              onClick={addWaypoint}
            >
              <FaPlus />
              <span>{t('events.add_waypoint')}</span>
            </button>
          </div>
        )}
        
        <div className="to-input-container">
          <label>{t('events.event_location')}</label>
          <div className="event-location-display">
            {eventLocation}
          </div>
        </div>
        
        <div className="calculate-button-container">
          <CustomButton6
            onClick={handleCalculateClick}
            label={
              isCalculating ? 
                t('events.calculating_route') : 
                t('events.calculate_route')
            }
            icon={isCalculating ? <FaSpinner className="spinner" /> : <FaRoute />}
            disabled={isCalculating}
          />
          
          {distance && (
            <div className="distance-display">
              <FaRoute />
              <span>{t('events.route_distance')}: {distance} km</span>
            </div>
          )}
        </div>
      </div>
      
      <div 
        className="event-route-map"
        ref={mapRef}
      />
    </div>
  );
});

export default EventRouteCalculator;